import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/api/auth'
import { meApi } from '@/api/me'
import type { LoginView, UserProfile } from '@/types/api'
import { clearAccessToken, setAccessToken, tokenState } from '@/utils/token'
import { clearLoggedOut, clearIdToken, markLoggedOut } from '@/utils/pkce'

/**
 * 登录/切换时的目标系统（`client_id`）。
 *
 * 只接受 `client_id` 而不是系统主键：登录页拿到的就是 OAuth2 协议标识，
 * 而自增主键可被枚举，前端拿着它去请求等于把"换个数字试试"的入口开给使用者。
 */
export type ClientIdInput = string | null | undefined

export const useUserStore = defineStore('user', () => {
  const profile = ref<UserProfile | null>(null)
  const logging = ref(false)

  const accessToken = computed(() => tokenState.accessToken.value)
  const permVersion = computed(() => tokenState.permVersion.value)
  const authenticated = computed(() => accessToken.value !== null)

  async function loadProfile(): Promise<UserProfile | null> {
    try {
      profile.value = await meApi.profile()
    } catch {
      profile.value = null
    }
    return profile.value
  }

  /**
   * 落库登录结果。
   *
   * 服务端会把"登录时选定的系统"回写在令牌里，这里直接把它同步到本地上下文：
   * 换账号登录时不留上一个账号的系统标识，菜单才不会按旧账号的可见范围去拉。
   */
  async function applyLoginResult(result: LoginView): Promise<LoginView> {
    if (result.tokens) {
      const tokens = result.tokens
      setAccessToken(tokens.accessToken, tokens.expiresIn, tokens.permVersion, {
        clientId: tokens.clientId ?? null,
        systemUuid: tokens.systemUuid ?? null,
        accountUuid: tokens.accountUuid ?? null,
      })
      // 登录成立：摘掉"刚登出"标记，此后才恢复 SSO 的免登体验
      clearLoggedOut()
      await loadProfile()
    }
    return result
  }

  function login(identifier: string, password: string, clientId?: ClientIdInput): Promise<LoginView> {
    logging.value = true
    return authApi.passwordLogin({
      identifier,
      password,
      channel: 'WEB',
      clientId: clientId ?? null,
    }).then(applyLoginResult).finally(() => {
      logging.value = false
    })
  }

  function loginByCode(target: string, code: string, clientId?: ClientIdInput): Promise<LoginView> {
    logging.value = true
    return authApi.codeLogin({
      target,
      code,
      channel: 'WEB',
      clientId: clientId ?? null,
    }).then(applyLoginResult).finally(() => {
      logging.value = false
    })
  }

  async function verifyMfa(pendingToken: string, code: string): Promise<LoginView> {
    return applyLoginResult(await authApi.mfaLogin(pendingToken, code))
  }

  async function resetPassword(pendingToken: string, newPassword: string): Promise<LoginView> {
    return applyLoginResult(await authApi.resetPassword(pendingToken, newPassword))
  }

  /**
   * 页面刷新后的静默续期（兜底）。
   *
   * access 已镜像到 sessionStorage，同一标签页刷新不丢；但跨标签页 / 隐私窗口或令牌过期时
   * 仍需靠 Cookie 里的 `uc_rt` 换回一张新的，用户不会察觉。
   */
  async function bootstrap(): Promise<boolean> {
    if (tokenState.accessToken.value) {
      return true
    }
    try {
      const pair = await authApi.refresh()
      setAccessToken(pair.accessToken, pair.expiresIn, pair.permVersion, {
        clientId: pair.clientId ?? null,
        systemUuid: pair.systemUuid ?? null,
        accountUuid: pair.accountUuid ?? null,
      })
      await loadProfile()
      return true
    } catch {
      return false
    }
  }

  /**
   * 切换**会话**所在系统：服务端按新系统重签令牌，这里同步本地状态。
   *
   * 失败不做本地降级 —— 系统上下文只能来自令牌，擅自留在本地会让
   * "界面在 B、令牌在 A"这种不一致长期存在，最后表现为菜单加载 403。
   *
   * 注意与管理后台的「管理对象选择」区分（stores/app.ts）：重签会把令牌里的系统改掉，
   * 控制台菜单与按钮权限随之漂到另一个系统上，因此管理后台的系统切换器**不应该**走这里。
   */
  /** 本地退场：清内存令牌、系统上下文与用户资料，服务端会话撤销由调用方负责。 */
  function clearLocalSession(): void {
    clearAccessToken()
    clearIdToken()
    profile.value = null
  }

  /**
   * 单端登出：撤销本设备的会话凭据（refresh + uc:session）并清本地。
   *
   * 服务端靠 Authorization 头里的 access token 定位"哪台设备"；access token 镜像在
   * sessionStorage，跨标签页 / 过期时可能为空，因此先补一次静默续期，换不到也不拦。
   */
  async function logout(): Promise<void> {
    try {
      if (!tokenState.accessToken.value) {
        try {
          const pair = await authApi.refresh()
          setAccessToken(pair.accessToken, pair.expiresIn, pair.permVersion, {
            clientId: pair.clientId ?? null,
            systemUuid: pair.systemUuid ?? null,
          })
        } catch {
          // 换不到令牌也继续登出：端点幂等，前端紧接着清本地，服务端残留交给 TTL
        }
      }
      await authApi.logout()
    } finally {
      clearLocalSession()
    }
  }

  /**
   * 退出登录（本设备）。
   *
   * @return 恒为 false：没有整页跳转，调用方继续做本地导航
   */
  async function endSession(): Promise<boolean> {
    await logout()
    // 兜底：即便服务端清理失败，守卫也不会再把用户静默送回授权流程
    markLoggedOut()
    return false
  }

  return {
    profile,
    logging,
    accessToken,
    permVersion,
    authenticated,
    login,
    loginByCode,
    verifyMfa,
    resetPassword,
    bootstrap,
    logout,
    endSession,
    loadProfile,
  }
})

